<template>
  <!-- The no-close-on-esc and no-close-on-backdrop props are important here, as the user will have to actively agree or cancel to close the modal -->
  <b-modal 
    ref="seating-messages-modal-ref" 
    hide-footer 
    hide-header 
    @hidden="handleModalHidden"
  >
    <div class="my-4">
      <h4 class="vtd-modal-title">Seating</h4>
      <button @click="closeModal(false)" type="button" class="close" aria-label="Close">
        <span aria-hidden="true"><i class="material-icons">&#xE5CD;</i></span>
      </button>
    </div>
    <div class="mb-5">
      <p>
        Airline seating policies for each of your flights are highlighted below – these are determined by your class
        of travel and specific booking class, which are also outlined below.<br /><br />
        We strongly encourage you read this information carefully and consider your airline seating preferences in
        advance of your trip, especially if travelling with younger children, as whilst airlines will try and sit
        you together, they are only obliged to sit children under 12 with one adult family member.<br /><br />
        If specific seats have not been pre-booked, you will still be able to select a seat at online check-in or at
        the check-in desk, subject to availability. Please note, however, that options are likely to be more limited
        and preferences or adjacent seats are by no means guaranteed.<br /><br />
        If Trailfinders have been able to pre-book your seating free of charge, this will have been discussed with
        you at the time of booking, and you will be able to view any assigned seats below.
      </p>
    </div>
    <div>
      <div v-if="groupedFlights && groupedFlights.length">
        <div v-for="(dateOfFlight, dateIndex) in groupedFlights" :key="dateOfFlight.date" class="mb-5">
          <h4>{{ dateOfFlight.date }}</h4>
          <div v-for="(flight, i) in dateOfFlight.flights" :key="`${dateOfFlight.date}-${i}`" class="pt-1 px-1 my-2">
            <b-row :class="`row-wrapper py-1 bg-grey ${!flight.visible && 'row-collapsed'
              }`">
              <b-col sm="1"><img v-if="flight.vendor"
                  :src="`https://www.trailfinders.com/graphics/flights/${flight.vendor}.gif`"
                  :alt="`${flight.vendor} logo`" />
                <i v-else class="icon-flight glyph-md" />
              </b-col>
              <b-col sm="2">
                <p class="font-weight-bold">{{ flight.flightno }}</p>
              </b-col>
              <b-col sm="5" class="d-flex flex-column">
                <span class="font-weight-bold">{{ flight.cityfrom }} to {{ flight.cityto }}</span>
                <span>With {{ flight.suppliername }}</span>
              </b-col>
              <b-col sm="3" class="d-flex flex-column">
                <span class="d-flex justify-content-between">
                  <p class="my-0">Departing</p>
                  <p class="my-0">{{ flight.deptime }}</p>
                </span>
                <span class="d-flex justify-content-between">
                  <p class="my-0">Arriving</p>
                  <p class="my-0">{{ flight.arrtime }}</p>
                </span>
                <span v-if="flight.vendorloc" class="d-flex justify-content-between font-weight-bold">
                  <p class="my-0">Airline Ref</p>
                  <p class="my-0">{{ flight.vendorloc }}</p>
                </span>
              </b-col>
              <b-col sm="1">
                <h2 @click="toggle(dateIndex, i)" :aria-controls="`accordion-${dateOfFlight.date}-${i}`"
                  class="p-2 toggle-btn font-weight-bold">
                  {{ flight.visible ? '-' : '+' }}
                </h2>
              </b-col>
            </b-row>
            <b-collapse :id="`accordion-${dateOfFlight.date}-${i}`" v-model="flight.visible">
              <div>
                <b-row class="row-wrapper" v-if="flight.baggageclass && flight.baggageclass != ''">
                  <b-col sm="3" class="col-wrapper font-weight-bold bg-grey pt-3px">Class of Travel</b-col>
                  <b-col sm="3" class="col-wrapper pt-3px">{{
                    titleCase(flight.baggageclass)
                  }}</b-col>
                  <b-col sm="3" class="col-wrapper font-weight-bold bg-grey pt-3px">Booking Class</b-col>
                  <b-col sm="3" class="py-1 pt-3px">{{ flight.classcode }}</b-col>
                </b-row>

                <b-row class="row-wrapper" v-if="flight.brandName && flight.brandName !== 'No Brand'">
                  <b-col sm="3" class="col-wrapper font-weight-bold bg-grey pt-3px">Brand Name</b-col>
                  <b-col sm="3">
                    {{titleCase(flight.brandName)}}
                  </b-col>
                </b-row>
                <div v-for="message in flight.flightMessages" :key="`${dateOfFlight.date}-${message.title}${flight.jsonid}`">
                  <b-row v-if="message.message" class="row-wrapper">
                    <b-col sm="3" class="border-right py-1 font-weight-bold bg-grey">{{ message.title }}</b-col>
                    <b-col sm="9" class="py-1 d-flex">
                      <p>
                        {{ message.message }}
                        <b-button v-if="showIssueFlightButton(message.code, flight.optiondate, message)" @click="scrollToTerms"
                          variant="info" class="issue-btn">How to issue my airline tickets
                        </b-button>
                        <br>
                      </p>
                    </b-col>
                  </b-row>
                </div>
                <b-row 
                  v-if="flight && flight.optiondate"
                  class="row-wrapper"
                >
                  <b-col sm="3" class="border-right py-1 font-weight-bold bg-grey">Ticket Issue Details</b-col>
                  <b-col sm="9" class="pt-1">
                    <b-row class="px-3 pt-2">
                      <p>
                        <strong><em>{{ formatIssueText(flight.optiondate) }}</em></strong>
                      </p>
                    </b-row>
                  </b-col>
                </b-row>
                <b-row 
                  v-if="flightsRawData && flightsRawData[0].flights && hasValidSeating(flight)"
                  class="row-wrapper"
                >
                  <b-col  
                    sm="3" 
                    class="border-right py-1 font-weight-bold bg-grey"
                  >
                    <p>Seats</p>
                  </b-col>
                  <b-col sm="9" class="pt-1">
                    {{ matchAndFormatSeatData(flight) }}
                  </b-col>
                </b-row>
              </div>
            </b-collapse>
          </div>
          <hr class="seperator" />
        </div>
        <!-- Agree statement section  -->
        <div id="issue-terms"
          :class="showIssueTermsSection() && showIssueTerms || modalOpenedFrom === 'paymentModal' ? '' : 'hidden'">
          <p class="font-weight-bold">Terms & Conditions</p>
          <div class="d-flex p-1">
            <p>In order to issue your airline tickets, allowing you to book your seats, you will need to pay the outstanding balance in full, 
              which makes it non-refundable. Any future changes you wish to make, if they are possible, will incur higher costs. 
              Please also note that any seating payments made to the airline are typically non-refundable if the airline changes or cancels the flight.
              <br />
            Please allow 24 hours from the confirmation of your payment for your airline tickets to be issued. We will confirm this to you via email.
          </p>
          </div>
          <div class="d-flex">
            <b-form-checkbox v-model="TCSAccepted" :value="true" :unchecked-value="false" class="p-1" />
            <p>
              I understand and accept the above conditions and would like to go ahead and pay for my trip in full and
              issue my airline tickets.
            </p>
          </div>
          <div class="d-flex">
            <b-button @click="closeModal(true)" variant="primary" :disabled="!TCSAccepted"
              :class="`mr-5 ${TCSAccepted ? '' : 'disabled'}`">Confirm</b-button>
            <b-button @click="closeModal(false)" variant="danger">Cancel</b-button>
            <p id="modal-end"></p>
          </div>
        </div>
      </div>
      <!--used as scroll target-->
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { getBooking } from '../../../booking/services';

export default {
  async created() {
    if (this.$route.query.emailRoute) {
      this.loginRes = await this.loginOnStart({
        username: this.$route.params.surname,
        bookingNumber: this.$route.params.bookingNumber,
        source: this.$route.params.source,
      });
      const response = await this.getBooking(
        this.$route.params.surname, 
        this.$route.params.bookingNumber, 
        this.$route.params.source
      )
      if (response) {
        this.accountData = response.account_information
        this.confirmedBooking = response.booking_information.fil === 'M' && true
        this.containsBookable = response.booking_itinerary.flights.some((flt) => {
          return flt && flt.flightMessages && flt.flightMessages.some((msg) => {
            if (!msg.code || !msg.canIssueEarly) return false
            if (!flt.optiondate) return true
            const optionDate = new Date(flt.optiondate)
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            return optionDate >= today
          })
        })
        this.updatePaymentFooterVisibility()
      }
    }
  },
  mounted() {
    this.agreed = false
    if (this.flightsWithSeatMessages && this.flightsWithSeatMessages.length) {
      this.groupByDate(this.flightsWithSeatMessages)
    }
  },
  watch: {
    flightsWithSeatMessages() {
      if (this.flightsWithSeatMessages && this.flightsWithSeatMessages.length) {
        this.groupByDate(this.flightsWithSeatMessages)
      }
    },
    modalOpenedFrom() {
      if (this.modalOpenedFrom === 'paymentModal') {
        this.showIssueTerms = true;
        setTimeout(() => {
          document
            .getElementById('modal-end')
            .scrollIntoView({ behavior: 'smooth' })
        }, 500);
      }
    }
  },
  data() {
    return {
      groupedFlights: [],
      visible: true,
      showPaymentFooter: false,
      agreed: false,
      TCSAccepted: false,
      showIssueTerms: false,
      from: '',
      modalOpenedFrom: '', // this may not be used, but there may be some components displayed if opened from say trip hub compared to opening from the payment screen. Clean up if not
      detailedFlightsLink: '',
      containsBookable: false,
      confirmedBooking: false,
      accountData: {}
    }
  },
  computed: {
    ...mapGetters({
      flightsWithSeatMessages: 'flights/getFlightsWithSeatingMessages',
      containsSeatBookableAfterIssue:
        'flights/getDoFlightsIncludeSeatBookableAfterIssue',
      accountInfo: 'booking/getAccount',
      getFil: "booking/getFil",
      flightsRawData: "flights/getFlights",
      hasEarlyTicketBlacklist: "flights/getHasEarlyTicketIssueBlacklist",
    }),
  },
  methods: {
    ...mapActions({
      loginOnStart: "auth/loginBookingNumberOnStartAction",
    }),
    showModal(params = null) {
      this.agreed = false
      this.modalOpenedFrom = ''
      this.updatePaymentFooterVisibility()
      this.$refs['seating-messages-modal-ref'].show()
      if (params && params.issueTickets) {
        this.scrollToTerms()
      }
    },
    closeModal(confirmed) {
      if (confirmed && this.TCSAccepted) {
        this.agreed = true
        this.$emit('issue-flights-response', true)        
      } else {
        this.agreed = false
        this.$emit('issue-flights-response', false)
      }
      this.$refs['seating-messages-modal-ref'].hide()
      this.modalOpenedFrom = ''
    },
    handleModalHidden() {
      if (!this.agreed) {
        this.TCSAccepted = false
        this.$emit('issue-flights-response', false)
      }
    },
    groupByDate(flts) {
      if (!flts.length) return
      let currDate = flts[0].groupdate
      let currArr = []
      const groupedArr = []
      flts.forEach((flt, i) => {
        flt.visible = true
        if (flt.groupdate === currDate) {
          currArr.push(flt)
        } else {
          groupedArr.push({ flights: [...currArr], date: currDate })
          currDate = flt.groupdate
          currArr = [flt]
        }

        if (i === flts.length - 1) {
          // last item
          groupedArr.push({ flights: [...currArr], date: currDate })
        }
      })
      this.groupedFlights = groupedArr
    },
    showIssueFlightButton(code, optiondate, messageObj) {
      return (
        !this.hasEarlyTicketBlacklist &&
        this.showPaymentFooter &&
        code &&
        messageObj.title === 'Seating Policy' &&
        messageObj.canIssueEarly &&
        new Date(optiondate) > new Date()
      )
    },
    showIssuedDate(optiondate) {
      return new Date(optiondate) < new Date()
    },
    showIssueTermsSection() {
      return (
        this.showPaymentFooter &&
        this.containsSeatBookableAfterIssue
      )
    },
    showIssueText(optiondate) {
      if (!optiondate || new Date(optiondate) > new Date()) {
        return false
      }
      return true
    },
    formatIssueText(optiondate) {
      const optionDate = new Date(optiondate).setHours(0,0,0,0)
      const today = new Date().setHours(0,0,0,0)
      if (optionDate >= today) {
        return `This ticket is due to be issued on ${new Date(optiondate).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`
      } else {
        return `This ticket was issued on ${new Date(optiondate).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}.`
      }
    },
    toggle(dateIndex, flightIndex) {
      const tmpGroupedFlights = [...this.groupedFlights]
      tmpGroupedFlights[dateIndex].flights[flightIndex].visible =
        !tmpGroupedFlights[dateIndex].flights[flightIndex].visible
      this.groupedFlights = tmpGroupedFlights
    },
    titleCase(str) {
      return str
        .split(' ')
        .map(
          (substring) =>
            substring.slice(0, 1).toUpperCase() +
            substring.slice(1).toLowerCase()
        )
        .join(' ')
    },
    scrollToTerms() {
      this.showIssueTerms = true;
      setTimeout(() => {
        document
          .getElementById('modal-end')
          .scrollIntoView({ behavior: 'smooth' })
      }, 500);
    },
    matchAndFormatSeatData(seatData) {
      // Finds matching flights from raw flight data and returns truncated seats string
      let fltData = {}
      this.flightsRawData.forEach((flight) => {
        flight.flights.forEach((flt) => {
          if (flt.controlNumber === seatData.vendorloc &&
              flt.departure &&
              flt.departure.airport &&
              flt.departure.airport.code === seatData.citycodefrom &&
              flt.departure.time === seatData.deptime
          ) {
            if (flt && flt.seats && flt.seats !== '') {
              fltData = flt.seats
            }
          }
        })
      })
      return fltData.substring(fltData.indexOf(": ") + 1).trim()
    },
    hasValidSeating(flightData) {
      // iterate through rawFltData finding the flight which matches the flight number and clients on the booking
      // need to iterate through each ticket as well as all flights on each ticket
      let hasValidSeats = false
      this.flightsRawData.forEach((flight) => {
        flight.flights.forEach((flt) => {
          if (flt.controlNumber === flightData.vendorloc &&
              flt.departure &&
              flt.departure.airport &&
              flt.departure.airport.code === flightData.citycodefrom &&
              flt.departure.time === flightData.deptime
          ) {
            if (flt && flt.seats && flt.seats !== '') {
              hasValidSeats = true
            }
          }
        })
      })
      return hasValidSeats
    },
    updatePaymentFooterVisibility() {
      if (
        (this.containsSeatBookableAfterIssue &&
        this.accountInfo &&
        this.accountInfo.balanceCostNoSymbol !== '0.00' &&
        this.getFil === 'M'
        )
        ||
        (this.accountData &&
          this.accountData.balanceCostNoSymbol !== '0.00' &&
          this.confirmedBooking &&
          this.containsBookable
        )) {
        this.showPaymentFooter = true
      }
    },
    getBooking
  },
}
</script>

<style scoped>
.toggle-btn {
  cursor: pointer;
}

.row-wrapper {
  border-top: 1px solid #dee2e6;
  border-left: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
}

.col-wrapper {
  border-right: 1px solid #dee2e6;
}

/* Only adds bottom border to the last row, so no double borders (from the border top and bottom) */
.row-wrapper:last-of-type,
.row-collapsed {
  border-bottom: 1px solid #dee2e6;
}

/* remove the right border on the last column to avoid double border */
.row-wrapper>.col-wrapper:last-child {
  border-right: 0;
}

.disabled {
  cursor: not-allowed;
}

.issue-btn {
  max-height: 2.5rem;
  border-radius: 5px;
  float: right;
  font-size: 0.8em;
  margin-top: 3px
}

.pt-3px {
  padding-top: 3px;
}

#issue-terms {
  padding-left: 15px;
}
</style>